import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CredentialEvaluation } from '@dosfilos/domain';
import type { CredentialProgress } from '../../../../lib/api/credential-progress.api';
import { formatDate } from '../../../../lib/i18n-format';
import { graduationMismatch, progressFor, type RecordAudience } from './academic-record.model';

// ─── Avance de la credencial ────────────────────────────────────────────────
// Para cada credencial del seminario: obligatorios, grupos, unidades y cuántos
// cursos faltan (estimado). Y, si el sistema de origen registra una
// graduación, ese dato AL LADO de la evaluación: cuando no coinciden se
// muestran los dos, sin que uno reemplace al otro. Esa comparación es para el
// registro académico: con `audience="student"` el alumno ve su graduación como
// dato y nada más (ver `progressFor`).

const dateOf = (ymd: string) => formatDate(`${ymd}T12:00:00`, { dateStyle: 'long' });

export function CredentialProgressTab({
  progress: full,
  audience = 'staff',
}: {
  progress: CredentialProgress;
  audience?: RecordAudience;
}) {
  const { t } = useTranslation('sis');
  const progress = progressFor(full, audience);
  const student = audience === 'student';
  const names = new Map<string, string>();
  for (const r of [...progress.rows, ...progress.inProgress]) names.set(r.courseCode, r.courseName);
  const mismatch = graduationMismatch(progress);

  return (
    <Stack spacing={3}>
      {progress.recordedGraduations.map((g, i) => (
        <Alert key={`${g.programId}-${i}`} severity="info" variant="outlined">
          <AlertTitle>
            {t(student ? 'academicRecord.student.graduated' : 'academicRecord.source.graduated')}
          </AlertTitle>
          {g.endedAt &&
            t(
              student ? 'academicRecord.student.graduatedOn' : 'academicRecord.source.graduatedOn',
              {
                date: dateOf(g.endedAt),
              },
            )}
          {g.note && (
            <Typography variant="body2" color="text.secondary">
              {t('academicRecord.source.note', { note: g.note })}
            </Typography>
          )}
        </Alert>
      ))}

      {mismatch && (
        <Alert severity="warning">
          <AlertTitle>{t('academicRecord.source.mismatchTitle')}</AlertTitle>
          <Typography variant="body2" gutterBottom>
            {t('academicRecord.source.mismatchBody', { credential: mismatch.credentialName })}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {mismatch.shortfall.requiredMissing.length > 0 && (
              <li>
                {t('academicRecord.source.missingRequired', {
                  courses: mismatch.shortfall.requiredMissing.join(', '),
                })}
              </li>
            )}
            {mismatch.shortfall.groups.map((g) => (
              <li key={g.courseCodes.join()}>
                {t('academicRecord.source.missingGroup', {
                  count: g.shortfall,
                  courses: g.courseCodes.join(', '),
                })}
              </li>
            ))}
            {mismatch.shortfall.unitsMissing > 0 && (
              <li>
                {t('academicRecord.source.missingUnits', {
                  count: mismatch.shortfall.unitsMissing,
                })}
              </li>
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            {t('academicRecord.source.mismatchBoth')}
          </Typography>
        </Alert>
      )}

      {progress.credentials.length === 0 ? (
        // El alumno graduado sin credencial evaluada ya vio su graduación arriba.
        student && progress.recordedGraduations.length > 0 ? null : (
          <Alert severity="info" variant="outlined">
            {t('academicRecord.credential.none')}
          </Alert>
        )
      ) : (
        progress.credentials.map((c) => <CredentialCard key={c.ruleId} c={c} names={names} />)
      )}
    </Stack>
  );
}

function CredentialCard({ c, names }: { c: CredentialEvaluation; names: Map<string, string> }) {
  const { t } = useTranslation('sis');
  const pct = c.unitsRequired > 0 ? Math.min(100, (c.unitsEarned / c.unitsRequired) * 100) : 100;
  const course = (code: string, met: boolean) => (
    <Tooltip key={code} title={names.get(code) ?? ''}>
      <Chip
        size="small"
        label={code}
        color={met ? 'success' : 'default'}
        variant={met ? 'filled' : 'outlined'}
        aria-label={t(
          met ? 'academicRecord.credential.courseMet' : 'academicRecord.credential.courseMissing',
          { course: names.get(code) ? `${code} ${names.get(code)}` : code },
        )}
      />
    </Tooltip>
  );

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={700}>
            {c.name}
          </Typography>
          <Chip
            label={t(
              c.eligible
                ? 'academicRecord.credential.eligible'
                : 'academicRecord.credential.notYet',
            )}
            color={c.eligible ? 'success' : 'default'}
            variant={c.eligible ? 'filled' : 'outlined'}
          />
        </Stack>

        <Typography variant="body2" fontWeight={600}>
          {t('academicRecord.credential.units', {
            earned: c.unitsEarned,
            required: c.unitsRequired,
          })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          aria-label={t('academicRecord.credential.units', {
            earned: c.unitsEarned,
            required: c.unitsRequired,
          })}
          sx={{ height: 8, borderRadius: 4, my: 1 }}
        />

        <Typography variant="subtitle2" fontWeight={700} mt={2} mb={1}>
          {t('academicRecord.credential.required', {
            met: c.requiredMet.length,
            total: c.requiredMet.length + c.requiredMissing.length,
          })}
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
          {c.requiredMet.map((code) => course(code, true))}
          {c.requiredMissing.map((code) => course(code, false))}
        </Stack>

        {c.groups.map((g) => (
          <Box key={g.courseCodes.join()} mt={2}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              {t('academicRecord.credential.core', { met: g.met.length, min: g.minCourses })}
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
              {g.courseCodes.map((code) => course(code, g.met.includes(code)))}
            </Stack>
          </Box>
        ))}

        {!c.eligible && (
          <Typography variant="body2" color="text.secondary" mt={2}>
            {t('academicRecord.credential.coursesMissing', { count: c.coursesMissing })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
